/**
 * CheckInModeTable - Simplified table for quick service check-ins
 * Features: Large touch targets, simplified layout, mobile-optimized
 */

import React from 'react';
import { CheckCircleIcon, ArrowUturnLeftIcon } from '@heroicons/react/24/outline';

const CheckInModeTable = ({ reservations, onCheckIn, onUndoCheckIn, formatDateTime }) => {
  // Filter reservations that can be checked in (confirmed, pending payment, or already attended)
  const confirmableReservations = reservations.filter(reservation =>
    reservation.status === 'CONFIRMED' ||
    reservation.status === 'ATTENDED' ||
    reservation.status === 'PENDING_PAYMENT'
  );

  if (confirmableReservations.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="text-gray-500 text-lg">No confirmed reservations available for check-in</div>
        <div className="text-gray-400 text-sm mt-2">Confirmed reservations will appear here when they're ready for check-in</div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {confirmableReservations.map((reservation) => {
        const { date, time } = formatDateTime(reservation.startTime);
        const customerName = reservation.customer
          ? `${reservation.customer.firstName} ${reservation.customer.lastName}`
          : 'Unknown Customer';

        const isCheckedIn = reservation.status === 'ATTENDED' || reservation.checkedInAt;
        // Use the actual reservation status to match normal reservations view
        const reservationStatus = reservation.status;

        // Check if checked in recently (within last 2 hours) to show undo option
        const checkedInRecently = isCheckedIn && reservation.checkedInAt &&
          (new Date() - new Date(reservation.checkedInAt)) < (2 * 60 * 60 * 1000);

        // Debug logging to troubleshoot undo button visibility
        if (isCheckedIn) {
          console.log('🔍 DEBUG UNDO BUTTON:', {
            reservationId: reservation.id,
            customerName,
            isCheckedIn,
            checkedInAt: reservation.checkedInAt,
            checkedInTime: reservation.checkedInAt ? new Date(reservation.checkedInAt) : null,
            timeDifference: reservation.checkedInAt ? (new Date() - new Date(reservation.checkedInAt)) : null,
            checkedInRecently,
            onUndoCheckIn: !!onUndoCheckIn
          });
        }

        return (
          <div
            key={reservation.id}
            className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm hover:shadow-md transition-shadow"
          >
            <div className="flex items-center justify-between space-x-4">
              {/* Customer and Service Info */}
              <div className="flex-1">
                <div className="flex items-center space-x-3">
                  <div className="text-2xl">👤</div>
                  <div>
                    <div className="text-lg font-semibold text-gray-900">
                      {customerName}
                    </div>
                    <div className="flex items-center space-x-2 mt-1">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        reservationStatus === 'CONFIRMED' || reservationStatus === 'ATTENDED'
                          ? 'bg-green-100 text-green-800'
                          : reservationStatus === 'ACTIVE'
                          ? 'bg-blue-100 text-blue-800'
                          : reservationStatus === 'COMPLETED'
                          ? 'bg-gray-100 text-gray-800'
                          : reservationStatus === 'CANCELLED'
                          ? 'bg-red-100 text-red-800'
                          : 'bg-yellow-100 text-yellow-800'
                      }`}>
                        {reservationStatus}
                      </span>
                    </div>
                    <div className="text-gray-600 mt-1">
                      <span className="font-bold">{reservation.service?.name || 'Unknown Service'}</span> • {date} {time}
                    </div>
                    {/* Attendees display */}
                    {(() => {
                      const participants = reservation.participantCount || 1;
                      let guestNames = [];

                      // Parse guest names if available
                      if (reservation.guestNames) {
                        try {
                          guestNames = JSON.parse(reservation.guestNames);
                        } catch (error) {
                          console.error('Error parsing guest names:', error);
                        }
                      }

                      return (
                        <div className="text-gray-500 text-sm mt-1">
                          <span className="font-medium">Attendees:</span> {participants} {participants === 1 ? 'person' : 'people'}
                          {guestNames && guestNames.length > 0 && (
                            <span className="ml-2">
                              • {guestNames.map(guest => `${guest.firstName} ${guest.lastName}`).join(', ')}
                            </span>
                          )}
                        </div>
                      );
                    })()}
                    {/* Add-ons display */}
                    {reservation.addons && reservation.addons.length > 0 && (
                      <div className="text-gray-500 text-sm mt-1">
                        <span className="font-medium">Add-ons:</span> {reservation.addons.map(addon => addon.addonName).join(', ')}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Check-in Button or Status */}
              <div className="flex-shrink-0">
                {isCheckedIn ? (
                  <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-3">
                    <div className="flex items-center space-x-2 text-green-600">
                      <CheckCircleIcon className="h-6 w-6" />
                      <div className="text-right">
                        <div className="font-medium">Checked in</div>
                        {reservation.checkedInAt && (
                          <div className="text-sm text-gray-500">
                            at {formatDateTime(reservation.checkedInAt).time}
                          </div>
                        )}
                      </div>
                    </div>
                    {checkedInRecently && onUndoCheckIn && (
                      <button
                        onClick={() => onUndoCheckIn(reservation.id)}
                        className="bg-yellow-600 hover:bg-yellow-700 text-white font-medium py-2 px-4 rounded-lg text-sm min-h-[36px] transition-colors focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:ring-offset-2 flex items-center justify-center space-x-1 w-full sm:w-auto"
                        title="Undo check-in (available for 2 hours after check-in)"
                      >
                        <ArrowUturnLeftIcon className="h-4 w-4" />
                        <span>Undo</span>
                      </button>
                    )}
                  </div>
                ) : (
                  <button
                    onClick={() => onCheckIn(reservation.id)}
                    className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-lg text-sm min-h-[36px] transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                  >
                    Check In
                  </button>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default CheckInModeTable;